import { ColumnHeader, download, generateCsv, mkConfig } from 'export-to-csv';
import { ColumnDef, Row } from '@tanstack/react-table';
import { ColumnDefMeta } from './types';
import { EXPANDABLE_COLUMN_ID } from './components/Expandable/ExpandableColumn';

export interface ExportTableOptions {
  filename?: string;
  fileHeaders?: ColumnHeader[];
  ignoredDataKeys?: string[];
  onlyDataKeys?: string[];
  decimalSeparator?: string;
}

export function handleExport<TData, TValue = unknown>(
  rows: Row<TData>[],
  columns: ColumnDef<TData, TValue>[],
  options: ExportTableOptions,
) {
  let columnHeaders: ColumnHeader[] = [];

  if (options.fileHeaders) {
    columnHeaders = options.fileHeaders;
  } else {
    for (const column of columns) {
      let columnKey: string | undefined;

      if ('accessorKey' in column && column.accessorKey) {
        columnKey = column.accessorKey as string;
      } else if (column.id) {
        columnKey = column.id;
      }

      if (!columnKey || [EXPANDABLE_COLUMN_ID, 'todoId'].includes(columnKey)) {
        continue;
      }

      const meta = column?.meta as ColumnDefMeta;

      if (
        (options.onlyDataKeys?.length &&
          options.onlyDataKeys.includes(columnKey) &&
          !meta?.ignoreOnExport) ||
        (options.ignoredDataKeys?.length &&
          !options.ignoredDataKeys.includes(columnKey) &&
          !meta?.ignoreOnExport) ||
        (!options.onlyDataKeys?.length &&
          !options.ignoredDataKeys?.length &&
          !meta?.ignoreOnExport)
      ) {
        columnHeaders.push({
          key: columnKey,
          displayLabel: column.header?.toString() || '',
        });
      }
    }
  }

  const filename = `${options.filename}__${new Date().toISOString().split('T')[0]}`;
  const csvConfig = mkConfig({
    quoteStrings: false,
    fieldSeparator: ';',
    filename,
    decimalSeparator: options.decimalSeparator || '.',
    useBom: true,
    columnHeaders,
  });

  function flattenRow<TData extends Record<string, unknown>>(
    row: TData,
  ): Record<keyof TData, string> {
    const flattened = {} as Record<keyof TData, string>;
    for (const key in row) {
      if (row[key] instanceof Date) {
        flattened[key] = row[key].toISOString();
      } else if (typeof row[key] === 'object' && row[key] !== null) {
        flattened[key] = JSON.stringify(row[key]);
      } else {
        flattened[key] = String(row[key]);
      }

      const column = columns.find(
        (col) =>
          col.id === key || ('accessorKey' in col && col.accessorKey === key),
      );
      const exportRenderer = (column?.meta as ColumnDefMeta)?.exportRenderer;
      if (exportRenderer) {
        flattened[key] = exportRenderer(flattened[key]);
      }
    }
    return flattened;
  }

  const csv = generateCsv(csvConfig)(
    rows.map((row) => flattenRow(row.original as Record<string, unknown>)),
  );
  download(csvConfig)(csv);
}
